/**
 * ai-categorize.js — AI Transaction Categorization Module for SPEKTRX
 *
 * Flow:
 * 1. Collect uncategorized cashTxs (manual deposits/withdrawals without .cat)
 * 2. Apply existing rules first (instant, no API call)
 * 3. Send unmatched batch to LLM for categorization
 * 4. Show review modal — user approves/edits each suggestion
 * 5. Approved categories saved on tx.cat + new rules created in S.aiRules
 *
 * Zero Supabase risk — AI calls go to user-configured endpoint only.
 */

import { S, save, stampItem } from '../core/state.js';
import { esc, uid, now, fByCur, promptSafe } from '../core/utils.js';
import { chatJSON, isAIConfigured } from '../core/ai.js';

// ─── CATEGORY TAXONOMY ─────────────────────────────────────────────

export const CATEGORIES = {
  // Income
  'Salary':           { group: 'Income',       icon: '💰', color: '#4ecdc4' },
  'Bonus':            { group: 'Income',       icon: '🎯', color: '#3b82f6' },
  'Freelance':        { group: 'Income',       icon: '💻', color: '#8b5cf6' },
  'Rental Income':    { group: 'Income',       icon: '🏠', color: '#06b6d4' },
  'Gift Received':    { group: 'Income',       icon: '🎁', color: '#ec4899' },
  'Tax Refund':       { group: 'Income',       icon: '🏛️', color: '#14b8a6' },
  'Other Income':     { group: 'Income',       icon: '📥', color: '#6366f1' },

  // Investment
  'Portfolio Buy':    { group: 'Investment',   icon: '📈', color: '#10b981' },
  'Trade Exit':       { group: 'Investment',   icon: '📊', color: '#f59e0b' },
  'Dividend':         { group: 'Investment',   icon: '💎', color: '#22c55e' },
  'Interest':         { group: 'Investment',   icon: '🏦', color: '#0ea5e9' },
  'DRIP':             { group: 'Investment',   icon: '🔄', color: '#84cc16' },

  // Transfer
  'Transfer In':      { group: 'Transfer',     icon: '➡️', color: '#6366f1' },
  'Transfer Out':     { group: 'Transfer',     icon: '⬅️', color: '#a855f7' },
  'FX Conversion':    { group: 'Transfer',     icon: '💱', color: '#f97316' },

  // Expenses
  'Tax Payment':      { group: 'Expense',      icon: '🏛️', color: '#ef4444' },
  'Major Purchase':   { group: 'Expense',      icon: '🛒', color: '#f43f5e' },
  'Living Expense':   { group: 'Expense',      icon: '🏠', color: '#fb7185' },
  'Subscription':     { group: 'Expense',      icon: '📱', color: '#fb923c' },
  'Insurance':        { group: 'Expense',      icon: '🛡️', color: '#64748b' },
  'Loan Payment':     { group: 'Expense',      icon: '📋', color: '#dc2626' },
  'Other Expense':    { group: 'Expense',      icon: '📤', color: '#94a3b8' },

  // Cash Flow
  'Reserve Fund':     { group: 'Cash Flow',    icon: '🏦', color: '#0891b2' },
  'Emergency':        { group: 'Cash Flow',    icon: '🚨', color: '#b91c1c' },
};

export const CATEGORY_NAMES = Object.keys(CATEGORIES);
export const CATEGORY_GROUPS = [...new Set(Object.values(CATEGORIES).map(c => c.group))];

/** Get category info (icon, color, group) with fallback */
export function getCategoryInfo(cat) {
  return CATEGORIES[cat] || { group: 'Other', icon: '•', color: 'var(--t3)' };
}

// ─── RULE ENGINE ───────────────────────────────────────────────────

/**
 * Try to match a cashTx against existing rules.
 * Rules match on: reason (exact), note (contains), type+reason combo.
 * Returns category string or null.
 */
export function matchRule(tx) {
  const rules = S.aiRules || [];
  if (!rules.length) return null;

  const txReason = (tx.reason || '').toLowerCase().trim();
  const txNote = (tx.note || '').toLowerCase().trim();
  const txType = tx.type || '';

  for (const rule of rules) {
    if (rule._del) continue;
    const pattern = (rule.pattern || '').toLowerCase().trim();
    if (!pattern) continue;

    // Exact reason match
    if (txReason === pattern) {
      rule.hitCount = (rule.hitCount || 0) + 1;
      return rule.category;
    }

    // Note contains pattern
    if (txNote && txNote.includes(pattern)) {
      rule.hitCount = (rule.hitCount || 0) + 1;
      return rule.category;
    }

    // Type + pattern match (e.g., "deposit:salary")
    if (rule.pattern.includes(':')) {
      const [rType, rPat] = rule.pattern.split(':', 2);
      if (txType === rType && txReason === rPat.toLowerCase()) {
        rule.hitCount = (rule.hitCount || 0) + 1;
        return rule.category;
      }
    }
  }
  return null;
}

/**
 * Auto-apply rules to all uncategorized transactions.
 * Returns count of newly categorized txs.
 */
export function applyRules() {
  let count = 0;
  const txs = (S.cashTxs || []).filter(tx => !tx._del && !tx.cat && _isManualTx(tx));
  for (const tx of txs) {
    const cat = matchRule(tx);
    if (cat) {
      tx.cat = cat;
      tx._updatedAt = Date.now();
      count++;
    }
  }
  return count;
}

/**
 * Create a new rule from an approved categorization.
 */
export function createRule(pattern, category, source = 'ai') {
  // Check for duplicate
  const existing = (S.aiRules || []).find(r =>
    !r._del && r.pattern.toLowerCase() === pattern.toLowerCase() && r.category === category
  );
  if (existing) return existing;

  const rule = stampItem({
    id: uid(),
    pattern,
    category,
    source, // 'ai' | 'manual'
    confidence: source === 'ai' ? 0.8 : 1.0,
    hitCount: 1,
    createdAt: now(),
  });
  if (!S.aiRules) S.aiRules = [];
  S.aiRules.push(rule);
  return rule;
}

// ─── HELPERS ───────────────────────────────────────────────────────

/** Check if a cashTx is a manual deposit/withdrawal (not system-generated) */
function _isManualTx(tx) {
  const t = tx.type;
  if (t === 'deposit' || t === 'withdraw') {
    const note = (tx.note || '').toLowerCase();
    const systemPrefixes = ['dividend:', 'drip reinvest:', 'expense:', 'undo expense:',
      'liability payment:', 'portfolio allocation', 'monthly ibkr deposit',
      'close daily td', 'maturity', 'salary:'];
    return !systemPrefixes.some(p => note.startsWith(p));
  }
  return false;
}

/** Get uncategorized manual transactions */
export function getUncategorized() {
  return (S.cashTxs || []).filter(tx => !tx._del && !tx.cat && _isManualTx(tx));
}

/** Get categorization stats */
export function getCatStats() {
  const manual = (S.cashTxs || []).filter(tx => !tx._del && _isManualTx(tx));
  const categorized = manual.filter(tx => tx.cat);
  return {
    total: manual.length,
    categorized: categorized.length,
    uncategorized: manual.length - categorized.length,
    ruleCount: (S.aiRules || []).filter(r => !r._del).length,
    pct: manual.length > 0 ? Math.round((categorized.length / manual.length) * 100) : 100,
  };
}

// ─── LLM CATEGORIZATION ───────────────────────────────────────────

/**
 * Send uncategorized transactions to LLM for categorization.
 * Returns array of { txId, category, confidence, reasoning }.
 *
 * @param {Array} txs - Transactions to categorize
 * @param {Object} opts - { signal }
 * @returns {Promise<Array<{txId: string, category: string, confidence: number, reasoning: string}>>}
 */
/**
 * How many transactions go to the model in one call. It is a token-budget limit, not a
 * statement about the work: whatever is left over is still uncategorized and the user is told
 * so, rather than being shown a default as if the model had chosen it.
 */
export const AI_CAT_BATCH = 30;

export async function categorizeWithAI(txs, opts = {}) {
  if (!isAIConfigured()) {
    throw new Error('AI not configured. Go to Settings → AI to set up your provider.');
  }

  if (!txs.length) return [];

  // Cap batch size to avoid token limits. EXPORTED, because the caller has to show the same
  // number it is actually sending — see openAICat. (audit 2026-08-03)
  const batch = txs.slice(0, AI_CAT_BATCH);

  // Build the prompt
  const txList = batch.map((tx, i) => {
    const amt = fByCur(tx.currency || 'USD', Math.abs(tx.amount || 0));
    // `reason` and `note` are FREE TEXT — the Cash modal's "Other" branch is deliberately so,
    // and both are interpolated into a pipe- and line-structured list. promptSafe. (2026-08-14)
    return `${i + 1}. [${promptSafe(tx.id, 40)}] ${promptSafe(tx.type, 24)} | ${amt} ${tx.currency || 'USD'} | reason: "${promptSafe(tx.reason, 60)}" | note: "${promptSafe(tx.note, 120)}" | date: ${(tx.date || '').slice(0, 10)}`;
  }).join('\n');

  const categoryList = CATEGORY_NAMES.join(', ');

  const messages = [
    {
      role: 'system',
      content: `You are a financial transaction categorizer. Given a list of personal finance transactions, assign each one to the most appropriate category.

Available categories: ${categoryList}

Rules:
- Each transaction gets exactly one category
- Use the reason and note fields as primary signals
- "Salary", "Bonus", "Freelance" deposits should map to their obvious categories
- Trade-related deposits (proceeds) → "Trade Exit"
- Dividend mentions → "Dividend"
- Transfers between accounts → "Transfer In" or "Transfer Out"
- Tax-related → "Tax Payment"
- Unknown withdrawals → "Other Expense"
- Unknown deposits → "Other Income"

Respond with a JSON object: { "results": [{ "id": "<tx id>", "category": "<category>", "confidence": <0.0-1.0>, "reasoning": "<brief reason>" }] }`
    },
    {
      role: 'user',
      content: `Categorize these ${batch.length} transactions:\n\n${txList}`
    }
  ];

  const result = await chatJSON(messages, { signal: opts.signal });

  // Map results back to txIds
  const aiResults = (result.data.results || result.data || []);

  return aiResults.map(r => ({
    txId: r.id,
    category: CATEGORY_NAMES.includes(r.category) ? r.category : 'Other Income',
    confidence: typeof r.confidence === 'number' ? r.confidence : 0.7,
    reasoning: r.reasoning || '',
  }));
}

// ─── REVIEW UI ─────────────────────────────────────────────────────

let _reviewModal = null;
let _pendingReview = []; // { tx, category, confidence, reasoning, approved }

/** Get the review overlay element, create if needed */
function _ensureReviewModal() {
  if (_reviewModal) return;

  _reviewModal = document.createElement('div');
  _reviewModal.id = 'moAICat';
  _reviewModal.className = 'mo';
  _reviewModal.innerHTML = `
    <div class="mod" style="max-width:680px;max-height:85vh;overflow:hidden;display:flex;flex-direction:column">
      <div class="mod-head">
        <h3 id="aiCatTitle">AI Categorization</h3>
        <button class="mod-x" data-action="closeAICat">&times;</button>
      </div>
      <div id="aiCatBody" style="overflow-y:auto;flex:1;padding:0 16px 16px"></div>
      <div id="aiCatFooter" class="mod-foot" style="display:flex;gap:8px;justify-content:space-between;padding:12px 16px;border-top:1px solid var(--brd)">
        <div id="aiCatStats" style="font-size:12px;color:var(--t3)"></div>
        <div style="display:flex;gap:8px">
          <button class="btn btn-s" data-action="aiCatApproveAll">Approve All</button>
          <button class="btn btn-s btn-p" data-action="aiCatSave">Save Approved</button>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(_reviewModal);

  // Backdrop click
  _reviewModal.addEventListener('click', (e) => {
    if (e.target === _reviewModal) closeAICat();
  });
}

/**
 * Open the AI categorization flow:
 * 1. Apply existing rules
 * 2. Send remaining to AI
 * 3. Show review modal
 */
export async function openAICat() {
  _ensureReviewModal();

  const body = document.getElementById('aiCatBody');
  const title = document.getElementById('aiCatTitle');
  const footer = document.getElementById('aiCatFooter');

  title.textContent = 'AI Categorization';
  footer.style.display = 'none';
  _reviewModal.classList.add('active');

  // Step 1: Apply rules first
  body.innerHTML = '<div style="padding:24px;text-align:center;color:var(--t3)"><div class="shimmer" style="width:200px;height:16px;margin:0 auto 8px;border-radius:4px"></div>Applying existing rules...</div>';

  await new Promise(r => setTimeout(r, 100)); // Let UI render

  const ruleCount = applyRules();
  const uncategorized = getUncategorized();

  if (uncategorized.length === 0) {
    body.innerHTML = `<div style="padding:32px;text-align:center">
      <div style="font-size:40px;margin-bottom:12px">✓</div>
      <div style="font-size:14px;font-weight:600;color:var(--t1)">All Transactions Categorized</div>
      <div style="font-size:12px;color:var(--t3);margin-top:4px">${ruleCount > 0 ? ruleCount + ' categorized by rules this time.' : 'Nothing to categorize.'}</div>
    </div>`;
    return;
  }

  // Step 2: Check AI configuration
  if (!isAIConfigured()) {
    body.innerHTML = `<div style="padding:24px;text-align:center">
      <div style="font-size:32px;margin-bottom:12px">🤖</div>
      <div style="font-size:14px;font-weight:600;color:var(--t1)">${uncategorized.length} Uncategorized Transaction${uncategorized.length > 1 ? 's' : ''}</div>
      ${ruleCount > 0 ? `<div style="font-size:12px;color:var(--grn);margin-top:4px">${ruleCount} categorized by rules.</div>` : ''}
      <div style="font-size:12px;color:var(--ora);margin-top:8px">AI not configured. Go to <strong>Settings → AI</strong> to set up your provider.</div>
      <div style="margin-top:16px"><button class="btn btn-s" data-action="aiCatManual">Categorize Manually</button></div>
    </div>`;
    footer.style.display = 'none';
    return;
  }

  // Step 3: Send to AI — one batch, and the screen says which batch.
  const _batch = uncategorized.slice(0, AI_CAT_BATCH);
  const _left = uncategorized.length - _batch.length;
  body.innerHTML = `<div style="padding:24px;text-align:center">
    <div class="shimmer" style="width:240px;height:16px;margin:0 auto 8px;border-radius:4px"></div>
    <div style="font-size:12px;color:var(--t3)">Sending ${_batch.length} of ${uncategorized.length} transaction${uncategorized.length > 1 ? 's' : ''} to AI...</div>
    ${ruleCount > 0 ? `<div style="font-size:11px;color:var(--grn);margin-top:4px">${ruleCount} already categorized by rules.</div>` : ''}
  </div>`;

  try {
    const results = await categorizeWithAI(_batch);

    // Only what was actually sent. This used to map over the WHOLE uncategorized list, so
    // every transaction past the 30th got `category: 'Other Income', confidence: 0` — a
    // default the model never produced — and then appeared in the review list beside the real
    // answers. Approve-all wrote those to the ledger tagged source:'ai'. A row the model never
    // saw must not be offered as its opinion. (audit 2026-08-03)
    _pendingReview = _batch.map(tx => {
      const aiResult = results.find(r => r.txId === tx.id);
      if (!aiResult) return null;      // the model skipped it: leave it uncategorized
      return {
        tx,
        category: aiResult.category,
        confidence: aiResult.confidence || 0,
        reasoning: aiResult.reasoning || '',
        approved: (aiResult.confidence || 0) >= 0.8, // Auto-approve high confidence
      };
    }).filter(Boolean);

    _renderReviewList(ruleCount, _left, _batch.length - _pendingReview.length);
  } catch (e) {
    body.innerHTML = `<div style="padding:24px;text-align:center">
      <div style="font-size:32px;margin-bottom:12px">⚠</div>
      <div style="font-size:14px;font-weight:600;color:var(--red)">AI Error</div>
      <div style="font-size:12px;color:var(--t3);margin-top:4px">${esc(e.message)}</div>
      <div style="margin-top:16px"><button class="btn btn-s" data-action="aiCatManual">Categorize Manually</button></div>
    </div>`;
  }
}

/** Render the review list */
function _renderReviewList(ruleCount, leftOver = 0, skipped = 0) {
  const body = document.getElementById('aiCatBody');
  const footer = document.getElementById('aiCatFooter');
  const stats = document.getElementById('aiCatStats');

  const approvedCount = _pendingReview.filter(r => r.approved).length;
  stats.textContent = `${approvedCount}/${_pendingReview.length} approved`;
  footer.style.display = 'flex';

  let html = '';
  // What did not make it into this pass, said before the list rather than not at all.
  if (leftOver > 0 || skipped > 0) {
    const bits = [];
    if (leftOver > 0) bits.push(`${leftOver} left for the next run (batch limit ${AI_CAT_BATCH})`);
    if (skipped > 0) bits.push(`${skipped} returned no answer and stay uncategorized`);
    html += `<div class="gls-soft" style="margin:0 0 10px;padding:8px 10px;border:1px solid var(--bd);border-left:3px solid var(--ora);border-radius:8px;font-size:11px;color:var(--t2)">${bits.join(' · ')}</div>`;
  }
  if (ruleCount > 0) {
    html += `<div style="padding:8px 0;font-size:11px;color:var(--grn)">✓ ${ruleCount} auto-categorized by rules</div>`;
  }

  _pendingReview.forEach((item, idx) => {
    const tx = item.tx;
    const amt = fByCur(tx.currency || 'USD', Math.abs(tx.amount || 0));
    const { icon: catIcon, color: catColor } = getCategoryInfo(item.category);
    const confPct = Math.round(item.confidence * 100);
    const confColor = item.confidence >= 0.8 ? 'var(--grn)' : item.confidence >= 0.5 ? 'var(--ora)' : 'var(--red)';

    html += `<div class="ai-cat-item ${item.approved ? 'approved' : ''}" data-idx="${idx}">
      <div class="ai-cat-check">
        <input type="checkbox" ${item.approved ? 'checked' : ''} data-action="aiCatToggle" data-args='["${idx}"]' />
      </div>
      <div class="ai-cat-info">
        <div class="ai-cat-tx">
          <span class="ai-cat-type" style="color:${catColor}">${catIcon} ${esc(tx.type)}</span>
          <span class="ai-cat-amt" style="color:${tx.amount >= 0 ? 'var(--grn)' : 'var(--red)'}">${tx.amount >= 0 ? '+' : ''}${amt} ${tx.currency || 'USD'}</span>
          <span class="ai-cat-date">${(tx.date || '').slice(0, 10)}</span>
        </div>
        <div class="ai-cat-note">${esc(tx.reason || tx.note || '—')}</div>
        ${item.reasoning ? `<div class="ai-cat-reason">${esc(item.reasoning)}</div>` : ''}
      </div>
      <div class="ai-cat-suggestion">
        <select class="ai-cat-select" data-action-change="aiCatChangeEvt" data-idx="${idx}">
          ${CATEGORY_NAMES.map(c => `<option value="${esc(c)}" ${c === item.category ? 'selected' : ''}>${esc(c)}</option>`).join('')}
        </select>
        <span class="ai-cat-conf" style="color:${confColor}">${confPct}%</span>
      </div>
    </div>`;
  });

  body.innerHTML = html;
}

/** Open manual categorization view (no AI, just the uncategorized list with dropdowns) */
export function aiCatManual() {
  _pendingReview = getUncategorized().map(tx => ({
    tx,
    category: 'Other Income',
    confidence: 0,
    reasoning: '',
    approved: false,
  }));
  _renderReviewList(0);
}

/** Toggle approval checkbox */
export function aiCatToggle(idx) {
  const i = typeof idx === 'string' ? parseInt(idx, 10) : idx;
  if (_pendingReview[i]) {
    _pendingReview[i].approved = !_pendingReview[i].approved;
    // Update stats
    const stats = document.getElementById('aiCatStats');
    const approvedCount = _pendingReview.filter(r => r.approved).length;
    if (stats) stats.textContent = `${approvedCount}/${_pendingReview.length} approved`;
    // Update item styling
    const item = document.querySelector(`.ai-cat-item[data-idx="${i}"]`);
    if (item) item.classList.toggle('approved', _pendingReview[i].approved);
  }
}

/** Change category for an item */
export function aiCatChange(idx, value) {
  const i = typeof idx === 'string' ? parseInt(idx, 10) : idx;
  if (_pendingReview[i]) {
    _pendingReview[i].category = value;
    _pendingReview[i].approved = true; // Auto-approve on manual change
    const item = document.querySelector(`.ai-cat-item[data-idx="${i}"]`);
    if (item) item.classList.add('approved');
    const cb = item?.querySelector('input[type="checkbox"]');
    if (cb) cb.checked = true;
    // Update stats
    const stats = document.getElementById('aiCatStats');
    const approvedCount = _pendingReview.filter(r => r.approved).length;
    if (stats) stats.textContent = `${approvedCount}/${_pendingReview.length} approved`;
  }
}

/** Handle change event from category select dropdown */
export function aiCatChangeEvt(e) {
  const idx = e.target.getAttribute('data-idx');
  const value = e.target.value;
  if (idx !== null) aiCatChange(idx, value);
}

/** Approve all items */
export function aiCatApproveAll() {
  _pendingReview.forEach(item => { item.approved = true; });
  _renderReviewList(0);
}

/** Save approved categorizations + create rules */
export function aiCatSave() {
  const approved = _pendingReview.filter(r => r.approved);
  if (!approved.length) {
    if (typeof window.toast === 'function') window.toast('Nothing approved', 'a-r');
    return;
  }

  let saved = 0;
  for (const item of approved) {
    const tx = S.cashTxs.find(t => t.id === item.tx.id);
    if (tx && !tx.cat) {
      tx.cat = item.category;
      tx._updatedAt = Date.now();
      saved++;

      // Create rule from the reason (if it has a meaningful reason)
      const reason = (tx.reason || '').trim();
      if (reason && reason !== '__other__' && reason.length > 1) {
        createRule(reason, item.category, item.confidence > 0 ? 'ai' : 'manual');
      }
    }
  }

  save();
  if (typeof window.markDirty === 'function') window.markDirty('cash');
  if (typeof window.renderC === 'function') window.renderC();

  closeAICat();
  if (typeof window.toast === 'function') {
    window.toast(`${saved} transaction${saved > 1 ? 's' : ''} categorized`, 'a-b');
  }
}

/** Close the review modal */
export function closeAICat() {
  if (_reviewModal) _reviewModal.classList.remove('active');
  _pendingReview = [];
}

// ─── CATEGORY BADGE RENDERER (used by cash.js) ────────────────────

/**
 * Render a small category badge for a cashTx row.
 * Returns HTML string.
 */
export function catBadge(cat) {
  if (!cat) return '';
  const info = getCategoryInfo(cat);
  return `<span class="cat-badge" style="--cat-color:${info.color}" title="${esc(cat)}">${esc(cat)}</span>`;
}
